import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, CheckCircle2, Info, X } from "lucide-react";
import {
  faultCost,
  formatDate,
  formatNumber,
  formatOdometer,
  formatReg,
  groupDefects,
  isFail,
  isPass,
  likelihoodTone,
  recallStatus,
  riskBand,
} from "../../lib/report";

// Status colours always come with an icon or a text label, never colour alone.
const TONES = {
  good: { pill: "bg-emerald-50 text-emerald-800 ring-emerald-600/20", icon: "text-emerald-600", bar: "bg-emerald-600", track: "bg-emerald-100", dot: "bg-emerald-600" },
  warning: { pill: "bg-amber-50 text-amber-800 ring-amber-600/25", icon: "text-amber-600", bar: "bg-amber-500", track: "bg-amber-100", dot: "bg-amber-500" },
  critical: { pill: "bg-red-50 text-red-800 ring-red-600/20", icon: "text-red-600", bar: "bg-red-600", track: "bg-red-100", dot: "bg-red-600" },
  neutral: { pill: "bg-slate-100 text-slate-700 ring-slate-500/20", icon: "text-slate-500", bar: "bg-slate-500", track: "bg-slate-200", dot: "bg-slate-400" },
};

const TONE_ICONS = { good: CheckCircle2, warning: AlertTriangle, critical: AlertTriangle, neutral: Info };

function tone(name) {
  return TONES[name] || TONES.neutral;
}

export function SectionTitle({ id, title, count, children }) {
  return (
    <div className="border-b border-slate-200 pb-4">
      <h2 id={id} className="text-xl font-bold tracking-tight text-slate-900">
        {title}
        {count !== undefined && <span className="ml-2 font-semibold text-slate-400">({count})</span>}
      </h2>
      {children && <p className="mt-1 text-sm text-slate-600">{children}</p>}
    </div>
  );
}

export function Pill({ tone: toneName = "neutral", icon: Icon, children }) {
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${tone(toneName).pill}`}
    >
      {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
      {children}
    </span>
  );
}

// A UK number plate, as used on the report header and search history.
export function Plate({ registration, size = "md" }) {
  const sizes = {
    sm: { plate: "h-7 text-sm", band: "w-4 text-[7px]", text: "px-2" },
    md: { plate: "h-9 text-lg", band: "w-5 text-[8px]", text: "px-3" },
    lg: { plate: "h-12 text-2xl sm:h-14 sm:text-[1.75rem]", band: "w-7 text-[10px]", text: "px-4" },
  }[size];

  return (
    <span
      className={`inline-flex shrink-0 items-stretch overflow-hidden rounded-md border border-slate-900/70 bg-[#FFD307] font-bold uppercase leading-none text-slate-900 shadow-sm ${sizes.plate}`}
    >
      <span className={`flex flex-col items-center justify-end bg-blue-700 pb-1 text-white ${sizes.band}`} aria-hidden="true">
        UK
      </span>
      <span className={`flex items-center whitespace-nowrap tracking-[0.06em] ${sizes.text}`}>{formatReg(registration)}</span>
    </span>
  );
}

export function RiskScore({ score, size = "lg" }) {
  const band = riskBand(score);
  const colours = tone(band.tone);
  const Icon = TONE_ICONS[band.tone];

  return (
    <div>
      <p className="flex items-baseline gap-1">
        <span className={`font-bold tracking-tight text-slate-900 ${size === "lg" ? "text-5xl" : "text-3xl"}`}>{score}</span>
        <span className="text-sm font-medium text-slate-500">/100</span>
      </p>
      <div className={`mt-3 h-2 w-full overflow-hidden rounded-full ${colours.track}`} aria-hidden="true">
        <div className={`h-full rounded-full ${colours.bar}`} style={{ width: `${Math.max(score, 3)}%` }} />
      </div>
      <p className="mt-2 flex flex-wrap items-center gap-x-1.5 text-sm font-medium text-slate-800">
        <Icon className={`h-4 w-4 ${colours.icon}`} aria-hidden="true" />
        {band.label}
        <span className="font-normal text-slate-500">· lower is better</span>
      </p>
    </div>
  );
}

// Label, value and an optional status line.
export function StatTile({ label, value, detail, tone: toneName, size = "md" }) {
  const Icon = toneName ? TONE_ICONS[toneName] : null;
  const small = size === "sm";
  return (
    <div className={`bg-white ${small ? "p-3" : "p-4"}`}>
      <p className={`${small ? "text-xs" : "text-sm"} text-slate-500`}>{label}</p>
      <p className={`mt-1 font-semibold text-slate-900 ${small ? "text-sm" : "text-lg"}`}>{value}</p>
      {detail && (
        <p className={`mt-1 flex items-start gap-1.5 text-slate-600 ${small ? "text-xs" : "text-sm"}`}>
          {Icon && (
            <Icon className={`shrink-0 ${small ? "mt-px h-3.5 w-3.5" : "mt-0.5 h-4 w-4"} ${tone(toneName).icon}`} aria-hidden="true" />
          )}
          <span>{detail}</span>
        </p>
      )}
    </div>
  );
}

function plural(count, singular, pluralForm = `${singular}s`) {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

// The headline facts for a report. The results page and the landing-page
// preview both use this, so they always say the same thing about the same data.
export function buildSummaryTiles({ mot, mileage, vehicleInfo }) {
  let motStatus;
  if (mot.expiry && !mot.expired) {
    motStatus = { label: "MOT status", value: "Valid", detail: `Until ${formatDate(mot.expiry)}`, tone: "good" };
  } else if (mot.expiry) {
    motStatus = { label: "MOT status", value: "Expired", detail: `Ran out on ${formatDate(mot.expiry)}`, tone: "critical" };
  } else if (mot.total) {
    motStatus = { label: "MOT status", value: "No valid MOT", detail: "No passed test on record", tone: "critical" };
  } else {
    motStatus = { label: "MOT status", value: "No MOT history", detail: "Most cars have their first MOT at three years old", tone: "neutral" };
  }

  let lastTest = { label: "Last MOT", value: "None on record", detail: null, tone: null };
  if (mot.latest) {
    const advisories = mot.latestDefects.advisories.length;
    const failures = mot.latestDefects.failures.length;
    lastTest = {
      label: "Last MOT",
      value: `${isPass(mot.latest) ? "Pass" : "Fail"} · ${formatDate(mot.latest.completedDate)}`,
      detail: failures
        ? `${plural(failures, "failure item")}, ${plural(advisories, "advisory", "advisories")}`
        : advisories
        ? plural(advisories, "advisory", "advisories")
        : "No advisories",
      tone: failures ? "critical" : advisories ? "warning" : "good",
    };
  }

  let mileageTile = { label: "Mileage at last MOT", value: "Not recorded", detail: null, tone: null };
  if (mileage.latest) {
    mileageTile = {
      label: "Mileage at last MOT",
      value: `${formatNumber(mileage.latest.miles)} miles`,
      detail: mileage.drops.length ? "Mileage has gone down" : "No drops recorded",
      tone: mileage.drops.length ? "critical" : "good",
    };
  }

  const recall = recallStatus(vehicleInfo.hasOutstandingRecall);
  const recallTile = {
    label: "Recalls",
    value: { good: "None outstanding", critical: "Outstanding recall", neutral: "Unknown" }[recall.tone],
    detail: {
      good: "According to the DVSA record",
      critical: "Ask a franchised dealer to check and fix it",
      neutral: "The DVSA couldn't confirm recall status",
    }[recall.tone],
    tone: recall.tone,
  };

  return { motStatus, lastTest, mileage: mileageTile, recall: recallTile };
}

// The four model ratings the AI analysis returns.
export function ModelRatings({ kpis }) {
  if (!kpis) return null;
  const items = [
    ["Reliability", kpis.reliability],
    ["Running costs", kpis.runningCosts],
    ["Typical repair", kpis.avgRepair],
    ["Best years", kpis.bestYears],
  ].filter(([, value]) => value);
  if (!items.length) return null;

  return (
    <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-4">
      {items.map(([label, value]) => (
        <div key={label} className="bg-white px-4 py-3">
          <dt className="text-xs text-slate-500">{label}</dt>
          <dd className="mt-1 text-base font-semibold text-slate-900">{String(value).replace(/(\d)\s*-\s*(\d)/, "$1–$2")}</dd>
        </div>
      ))}
    </dl>
  );
}

// ---------- Faults ----------

function RankBadge({ rank }) {
  return (
    <span
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold tabular-nums text-slate-700"
      aria-hidden="true"
    >
      {rank}
    </span>
  );
}

function SeverityScale({ grade }) {
  const value = Math.max(1, Math.min(5, Math.round(grade)));
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="flex gap-0.5" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((step) => (
          <span key={step} className={`h-2.5 w-1.5 rounded-sm ${step <= value ? "bg-slate-700" : "bg-slate-200"}`} />
        ))}
      </span>
      Severity {value}/5
    </span>
  );
}

function FaultItem({ fault, rank, compact }) {
  const likelihood = likelihoodTone(fault.likelihood);
  const cost = faultCost(fault);
  const severity = Number(fault.severityGrade);
  const risk = Number(fault.riskFactor);

  if (compact) {
    return (
      <li className="flex items-start gap-3 py-3">
        <RankBadge rank={rank} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-5 text-slate-900">{fault.problem}</p>
          {fault.location && <p className="mt-0.5 text-xs text-slate-500">{fault.location}</p>}
        </div>
        <div className="shrink-0 text-right">
          {likelihood && <Pill tone={likelihood}>{fault.likelihood}</Pill>}
          {cost && <p className="mt-1 text-xs font-medium tabular-nums text-slate-700">{cost}</p>}
        </div>
      </li>
    );
  }

  return (
    <li className="flex gap-4 py-6 first:pt-0 last:pb-0">
      <RankBadge rank={rank} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div>
            <h3 className="text-base font-semibold text-slate-900">{fault.problem}</h3>
            {fault.location && <p className="mt-0.5 text-sm text-slate-500">{fault.location}</p>}
          </div>
          {cost && (
            <div className="shrink-0 sm:text-right">
              <p className="text-xs text-slate-500">Estimated repair</p>
              <p className="text-base font-semibold text-slate-900">{cost}</p>
            </div>
          )}
        </div>

        {(likelihood || severity > 0 || risk > 0) && (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-600">
            {likelihood && <Pill tone={likelihood}>{fault.likelihood} likelihood</Pill>}
            {severity > 0 && <SeverityScale grade={severity} />}
            {risk > 0 && <span>Risk {risk}%</span>}
          </div>
        )}

        {fault.symptoms && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-slate-900">What to check</h4>
            <p className="mt-1 text-sm leading-6 text-slate-700">{fault.symptoms}</p>
          </div>
        )}
        {fault.recommendedFix && (
          <div className="mt-3">
            <h4 className="text-sm font-semibold text-slate-900">Usual fix</h4>
            <p className="mt-1 text-sm leading-6 text-slate-700">{fault.recommendedFix}</p>
          </div>
        )}
      </div>
    </li>
  );
}

export function FaultList({ faults, compact = false }) {
  return (
    <ol className={compact ? "divide-y divide-slate-100" : "divide-y divide-slate-200"}>
      {faults.map((fault, index) => (
        <FaultItem key={`${fault.problem}-${index}`} fault={fault} rank={index + 1} compact={compact} />
      ))}
    </ol>
  );
}

// ---------- MOT history ----------

export function ResultPill({ test }) {
  if (isPass(test)) return <Pill tone="good" icon={Check}>Pass</Pill>;
  if (isFail(test)) return <Pill tone="critical" icon={X}>Fail</Pill>;
  return <Pill>Result unknown</Pill>;
}

function DefectGroup({ title, items, tone: toneName }) {
  if (!items.length) return null;
  return (
    <div>
      <p className="font-medium text-slate-900">
        {title} ({items.length})
      </p>
      <ul className="mt-1.5 space-y-1.5 text-slate-700">
        {items.map((defect, index) => (
          <li key={`${defect.text}-${index}`} className="flex gap-2.5">
            <span className={`mt-[0.45rem] h-1.5 w-1.5 shrink-0 rounded-full ${tone(toneName).dot}`} aria-hidden="true" />
            <span>
              {defect.text}
              {defect.dangerous && (
                <span className="ml-2 align-middle">
                  <Pill tone="critical">Dangerous</Pill>
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DefectGroups({ defects }) {
  const groups = groupDefects(defects);
  if (!Object.values(groups).some((group) => group.length)) {
    return <p className="text-slate-500">No defects or advisories recorded.</p>;
  }
  return (
    <div className="space-y-3">
      <DefectGroup title="Failure items" items={groups.failures} tone="critical" />
      <DefectGroup title="Repaired during the test" items={groups.repaired} tone="warning" />
      <DefectGroup title="Minor defects" items={groups.minor} tone="warning" />
      <DefectGroup title="Advisories" items={groups.advisories} tone="neutral" />
    </div>
  );
}

export function MotTestList({ tests }) {
  return (
    <ol className="space-y-4">
      {tests.map((test, index) => (
        <li key={test.motTestNumber || `${test.completedDate}-${index}`} className="rounded-lg border border-slate-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-slate-100 px-4 py-3">
            <div className="flex items-center gap-3">
              <ResultPill test={test} />
              <span className="font-semibold text-slate-900">{formatDate(test.completedDate)}</span>
            </div>
            <span className="text-sm tabular-nums text-slate-600">{formatOdometer(test)}</span>
          </div>
          <div className="space-y-3 px-4 py-3 text-sm">
            <DefectGroups defects={test.defects} />
            {isPass(test) && test.expiryDate && (
              <p className="text-xs text-slate-500">Expiry date {formatDate(test.expiryDate)}</p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

// ---------- Mileage ----------

function useElementWidth(ref) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const update = () => setWidth(element.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

function niceScale(max) {
  const rough = Math.max(max, 1) / 4;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].map((multiple) => multiple * magnitude).find((value) => value >= rough);
  return { step, max: Math.ceil(max / step) * step };
}

const CHART_HEIGHT = 240;
const MARGIN = { top: 24, right: 20, bottom: 32, left: 60 };

// Odometer reading at each MOT. Single series, so the heading names it and
// there's no legend. Hover or use the arrow keys for exact readings.
export function MileageChart({ readings }) {
  const wrapperRef = useRef(null);
  const width = useElementWidth(wrapperRef);
  const [active, setActive] = useState(null);

  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;
  const start = readings[0].date.getTime();
  const end = readings[readings.length - 1].date.getTime();
  const span = Math.max(end - start, 1);
  const { max: yMax, step } = niceScale(Math.max(...readings.map((reading) => reading.miles)));

  const xFor = (time) => MARGIN.left + ((time - start) / span) * innerWidth;
  const yFor = (miles) => MARGIN.top + innerHeight - (miles / yMax) * innerHeight;
  const points = readings.map((reading) => ({ ...reading, x: xFor(reading.date.getTime()), y: yFor(reading.miles) }));
  const baseline = yFor(0);
  const linePath = points.map((point, index) => `${index ? "L" : "M"}${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${points[points.length - 1].x.toFixed(1)},${baseline} L${points[0].x.toFixed(1)},${baseline} Z`;

  const yTicks = [];
  for (let value = 0; value <= yMax; value += step) yTicks.push(value);

  const firstYear = new Date(start).getFullYear();
  const lastYear = new Date(end).getFullYear();
  let xTicks = [];
  for (let year = firstYear + 1; year <= lastYear; year += 1) {
    xTicks.push({ time: new Date(year, 0, 1).getTime(), label: String(year) });
  }
  const maxLabels = Math.max(2, Math.floor(innerWidth / 52));
  if (xTicks.length > maxLabels) {
    const every = Math.ceil(xTicks.length / maxLabels);
    xTicks = xTicks.filter((_, index) => index % every === 0);
  }
  if (!xTicks.length) {
    xTicks = [
      { time: start, label: formatDate(readings[0].date) },
      { time: end, label: formatDate(readings[readings.length - 1].date) },
    ];
  }

  const activePoint = active === null ? null : points[active];
  const last = points[points.length - 1];

  const handlePointerMove = (event) => {
    const x = event.clientX - event.currentTarget.getBoundingClientRect().left;
    let nearest = 0;
    points.forEach((point, index) => {
      if (Math.abs(point.x - x) < Math.abs(points[nearest].x - x)) nearest = index;
    });
    setActive(nearest);
  };

  const handleKeyDown = (event) => {
    const moves = { ArrowLeft: -1, ArrowRight: 1, Home: -Infinity, End: Infinity };
    if (!(event.key in moves)) return;
    event.preventDefault();
    setActive((current) => {
      const from = current === null ? points.length - 1 : current;
      return Math.min(points.length - 1, Math.max(0, from + moves[event.key]));
    });
  };

  const tooltipWidth = 184;
  const tooltipLeft = activePoint ? Math.min(Math.max(activePoint.x - tooltipWidth / 2, 0), width - tooltipWidth) : 0;
  const tooltipTop = activePoint ? (activePoint.y > 84 ? activePoint.y - 78 : activePoint.y + 14) : 0;

  return (
    <div
      ref={wrapperRef}
      className="relative rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
      tabIndex={0}
      role="group"
      aria-label="Mileage recorded at each MOT. Use the left and right arrow keys to step through the readings."
      onKeyDown={handleKeyDown}
      onFocus={() => setActive((current) => (current === null ? points.length - 1 : current))}
      onBlur={() => setActive(null)}
    >
      {width > 0 && (
        <svg
          width={width}
          height={CHART_HEIGHT}
          className="block touch-pan-y select-none"
          onPointerMove={handlePointerMove}
          onPointerLeave={() => setActive(null)}
          aria-hidden="true"
        >
          {yTicks.map((value) => (
            <g key={value}>
              <line
                x1={MARGIN.left}
                x2={width - MARGIN.right}
                y1={yFor(value)}
                y2={yFor(value)}
                stroke={value === 0 ? "#cbd5e1" : "#e2e8f0"}
                strokeWidth="1"
              />
              <text x={MARGIN.left - 10} y={yFor(value)} dy="0.32em" textAnchor="end" className="fill-slate-500 text-[12px] tabular-nums">
                {formatNumber(value)}
              </text>
            </g>
          ))}

          {xTicks.map((tick) => (
            <text key={tick.time} x={xFor(tick.time)} y={CHART_HEIGHT - 8} textAnchor="middle" className="fill-slate-500 text-[12px] tabular-nums">
              {tick.label}
            </text>
          ))}

          <path d={areaPath} fill="#2563eb" fillOpacity="0.08" />
          <path d={linePath} fill="none" stroke="#2563eb" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

          {activePoint && (
            <line x1={activePoint.x} x2={activePoint.x} y1={MARGIN.top} y2={baseline} stroke="#94a3b8" strokeWidth="1" />
          )}

          {points.map((point, index) => (
            <circle
              key={`${point.date.getTime()}-${index}`}
              cx={point.x}
              cy={point.y}
              r={index === active ? 6 : 4}
              fill={point.dropped ? "#dc2626" : "#2563eb"}
              stroke="#ffffff"
              strokeWidth="2"
            />
          ))}

          {activePoint === null && (
            <text
              x={last.x}
              y={last.y - 12}
              textAnchor="end"
              stroke="#ffffff"
              strokeWidth="4"
              paintOrder="stroke"
              className="fill-slate-700 text-[12px] font-semibold tabular-nums"
            >
              {formatNumber(last.miles)}
            </text>
          )}
        </svg>
      )}

      {activePoint && (
        <div
          className="pointer-events-none absolute z-10 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-lg"
          style={{ left: tooltipLeft, top: tooltipTop, width: tooltipWidth }}
        >
          <p className="font-semibold tabular-nums text-slate-900">{formatNumber(activePoint.miles)} miles</p>
          <p className="text-slate-600">
            {formatDate(activePoint.date)} · {activePoint.passed ? "Pass" : "Fail"}
          </p>
          {activePoint.dropped && (
            <p className="mt-1 flex items-center gap-1 text-xs font-medium text-red-700">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
              Lower than an earlier reading
            </p>
          )}
        </div>
      )}

      <p className="sr-only" aria-live="polite">
        {activePoint ? `${formatDate(activePoint.date)}: ${formatNumber(activePoint.miles)} miles` : ""}
      </p>
    </div>
  );
}

export function MileageTable({ readings }) {
  return (
    <details className="text-sm">
      <summary className="cursor-pointer select-none font-medium text-blue-700 hover:text-blue-800">
        Show readings as a table
      </summary>
      <table className="mt-3 w-full text-left">
        <thead>
          <tr className="border-b border-slate-200 text-slate-500">
            <th scope="col" className="py-2 font-medium">
              MOT date
            </th>
            <th scope="col" className="py-2 text-right font-medium">
              Mileage
            </th>
          </tr>
        </thead>
        <tbody>
          {[...readings].reverse().map((reading, index) => (
            <tr key={`${reading.date.getTime()}-${index}`} className="border-b border-slate-100 last:border-0">
              <td className="py-2 text-slate-700">{formatDate(reading.date)}</td>
              <td className="py-2 text-right tabular-nums text-slate-900">
                {formatNumber(reading.miles)}
                {reading.dropped && <span className="ml-2 text-xs font-medium text-red-700">Drop</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

// Plain-English checks on the mileage record, plus the mileage the user entered.
export function mileageFindings({ readings, drops, latest, highest, perYear, entered }) {
  const findings = [];
  if (!readings.length) return findings;

  if (drops.length) {
    const { reading, previous } = drops[0];
    findings.push({
      tone: "critical",
      title: drops.length > 1 ? `Mileage went down ${drops.length} times between MOTs` : "Mileage went down between MOTs",
      text: `${formatNumber(reading.miles)} miles on ${formatDate(reading.date)} is lower than the ${formatNumber(
        previous.miles
      )} miles recorded on ${formatDate(previous.date)}. Ask the seller to explain before you buy.`,
    });
  } else if (readings.length === 1) {
    findings.push({
      tone: "neutral",
      title: "One MOT reading so far",
      text: `${formatNumber(latest.miles)} miles on ${formatDate(latest.date)}.`,
    });
  } else {
    findings.push({
      tone: "good",
      title: "No drops in recorded mileage",
      text: `All ${readings.length} MOT readings go up${
        perYear ? `, averaging about ${formatNumber(perYear)} miles a year` : ""
      }.`,
    });
  }

  if (entered !== null && highest) {
    if (entered < highest.miles) {
      findings.push({
        tone: "critical",
        title: "Mileage you entered is lower than the MOT record",
        text: `You entered ${formatNumber(entered)} miles, but ${formatNumber(highest.miles)} miles was recorded on ${formatDate(
          highest.date
        )}.`,
      });
    } else {
      findings.push({
        tone: "neutral",
        title: `You entered ${formatNumber(entered)} miles`,
        text: `That's ${formatNumber(entered - latest.miles)} more than at the last MOT on ${formatDate(latest.date)}.`,
      });
    }
  }

  return findings;
}

export function Finding({ tone: toneName, title, text }) {
  const Icon = TONE_ICONS[toneName];
  return (
    <div className="flex gap-3">
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${tone(toneName).icon}`} aria-hidden="true" />
      <div>
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <p className="mt-0.5 text-sm leading-6 text-slate-600">{text}</p>
      </div>
    </div>
  );
}
